import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { School, Verified, Schedule, Edit } from '@mui/icons-material';
import { CaretakerProfile } from '../services/caretakerProfileService';

interface CaretakerCertificationsProps {
  certifications: CaretakerProfile['certifications'];
  isOwner?: boolean;
  onManageCertifications?: () => void;
}

const CaretakerCertifications: React.FC<CaretakerCertificationsProps> = ({ certifications, isOwner = false, onManageCertifications }) => {
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Date not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  const isExpired = (expiryDate: string | null): boolean => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate: string | null): boolean => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return expiry <= threeMonthsFromNow && expiry > new Date();
  };

  const getStatusColor = (cert: CaretakerProfile['certifications'][0]): 'success' | 'warning' | 'error' | 'default' => {
    if (!cert.verified) return 'default';
    if (isExpired(cert.expiryDate)) return 'error';
    if (isExpiringSoon(cert.expiryDate)) return 'warning';
    return 'success';
  };

  const getStatusText = (cert: CaretakerProfile['certifications'][0]): string => {
    if (!cert.verified) return 'Unverified';
    if (isExpired(cert.expiryDate)) return 'Expired';
    if (isExpiringSoon(cert.expiryDate)) return 'Expiring Soon';
    return 'Verified';
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School />
            Certifications & Licenses
          </Typography>
          {isOwner && onManageCertifications && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={onManageCertifications}
              size="small"
            >
              Manage Certifications
            </Button>
          )}
        </Box>
        
        {certifications.length > 0 ? (
          <Grid container spacing={2}>
            {certifications.map((cert, index) => (
              <React.Fragment key={cert.id}>
                <Grid item xs={12}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6">
                        {cert.name}
                      </Typography>
                      <Chip
                        icon={cert.verified ? <Verified /> : undefined}
                        label={getStatusText(cert)}
                        color={getStatusColor(cert)}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Issued by: {cert.issuer}
                    </Typography>
                    
                    {cert.credentialId && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Credential ID: {cert.credentialId}
                      </Typography>
                    )}
                    
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          Issued: {formatDate(cert.issueDate)}
                        </Typography>
                      </Box>
                      
                      {cert.expiryDate && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography 
                            variant="caption" 
                            color={isExpired(cert.expiryDate) ? 'error.main' : isExpiringSoon(cert.expiryDate) ? 'warning.main' : 'text.secondary'}
                          >
                            Expires: {formatDate(cert.expiryDate)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
                {index < certifications.length - 1 && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No certifications listed yet.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CaretakerCertifications;
